import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';
import {
    Box,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Typography,
    CircularProgress,
} from '@mui/material';
import Modal from '../../general/modal';

export interface CopyInvestigationPayload {
    name: string;
    copyDepartments: boolean;
    copyRoles: boolean;
    copyInsuranceField: boolean;
}

interface CopyInvestigationModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (payload: CopyInvestigationPayload) => void;
    /** Submission in flight — disables the form and shows a spinner. */
    loading?: boolean;
    /** Error to show inline (form stays editable to retry). String or a node (e.g. <Translate/>). */
    error?: React.ReactNode;
    /** Optional initial value for the name field (e.g. "Copy of <source>"). */
    defaultName?: string;
}

/**
 * SHER-576 — the Dashboard "Copy platform" modal.
 *
 * Presentational only: it collects the new platform name and the optional
 * per-section clone flags (departments, custom roles, insurance field) and hands
 * them to `onSubmit`. The parent owns the API call and drives loading/error, so
 * this component stays trivially testable and Storybook-renderable.
 */
export default function CopyInvestigationModal({
    open,
    onClose,
    onSubmit,
    loading = false,
    error = null,
    defaultName = '',
}: CopyInvestigationModalProps) {
    const [name, setName] = useState(defaultName);
    const [copyDepartments, setCopyDepartments] = useState(false);
    const [copyRoles, setCopyRoles] = useState(false);
    const [copyInsuranceField, setCopyInsuranceField] = useState(false);

    const trimmedName = name.trim();
    const canSubmit = trimmedName.length > 0 && !loading;

    const handleSubmit = () => {
        if (!canSubmit) return;
        onSubmit({ name: trimmedName, copyDepartments, copyRoles, copyInsuranceField });
    };

    return (
        <Modal
            open={open}
            closeModal={onClose}
            title={<Translate id="investigation.copy.title" />}
            confirmAction={handleSubmit}
            confirmButtonLabel="investigation.copy.submit"
            disabled={!canSubmit}
            maxWidth="sm"
            fullWidth
        >
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                <Typography variant="body2" color="textSecondary">
                    <Translate id="investigation.copy.description" />
                </Typography>

                <TextField
                    label={<Translate id="investigation.copy.nameLabel" />}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    autoFocus
                    disabled={loading}
                    inputProps={{ 'data-testid': 'copy-investigation-name' }}
                />

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={copyDepartments}
                                onChange={(e) => setCopyDepartments(e.target.checked)}
                                disabled={loading}
                                inputProps={{ 'data-testid': 'copy-departments' } as any}
                            />
                        }
                        label={<Translate id="investigation.copy.departments" />}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={copyRoles}
                                onChange={(e) => setCopyRoles(e.target.checked)}
                                disabled={loading}
                                inputProps={{ 'data-testid': 'copy-roles' } as any}
                            />
                        }
                        label={<Translate id="investigation.copy.roles" />}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={copyInsuranceField}
                                onChange={(e) => setCopyInsuranceField(e.target.checked)}
                                disabled={loading}
                                inputProps={{ 'data-testid': 'copy-insurance' } as any}
                            />
                        }
                        label={<Translate id="investigation.copy.insurance" />}
                    />
                </FormGroup>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24} data-testid="copy-loading" />
                    </Box>
                )}

                {error && (
                    <Typography color="error" data-testid="copy-error">
                        {error}
                    </Typography>
                )}
            </Box>
        </Modal>
    );
}
